import { UntilDestroy } from "@ngneat/until-destroy";
import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap, take } from "rxjs";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { AccountService } from "@modules/account/services/account.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Router } from "@angular/router";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { calculateCashBalance } from "./cashplus-widget-util";
import { PortfolioAssetDetails } from "@modules/portfolio/domain/entities/portfolio-overview.model";
import { FiatService } from "@main-services/currency";
import { CashPlusTrackingFacade } from "@modules/cashplus/+state/cashplus-tracking.facade";
import { filter } from "rxjs/operators";

export interface CashplusWidgetViewModel {
	cashBalance: number;
	cashPlusBalance: number;
	totalRewards: number;
	defaultFiatId: string;
	error: boolean;
	isPortfolioHidden?: boolean;
}

@UntilDestroy()
@Injectable()
export class CashPlusWidgetFacade {
	private isLoadingInProgress$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isLoadingApi$: Observable<boolean> = this.isLoadingInProgress$$.asObservable();

	public constructor(
		private readonly cashplusFacade: CashPlusFacade,
		private accountService: AccountService,
		private fiatWalletService: FiatWalletService,
		private readonly router: Router,
		private readonly portfolioFacade: PortfolioFacade,
		private readonly cashPlusPortfolioFacade: CashplusPortfolioFacade,
		private readonly fiatService: FiatService,
		private readonly trackingFacade: CashPlusTrackingFacade
	) {}

	public getViewModel$(): Observable<CashplusWidgetViewModel> {
		return combineLatest([
			this.cashPlusPortfolioFacade.getPortfolioState$(),
			this.cashplusFacade.getConfigurationsState$(),
			this.portfolioFacade.isPortfolioDataHidden$
		]).pipe(
			map(([portfolio, configurations, isPortfolioHidden]) => {
				return {
					...calculateCashBalance(
						portfolio,
						configurations,
						this.accountService.getDefaultFiat(),
						this.fiatService.list
					),
					isPortfolioHidden
				};
			})
		);
	}

	public showCashplusWidget$(): Observable<boolean> {
		return this.cashplusFacade.isCashPlusEnabled$().pipe(
			take(1),
			switchMap((isCashPlusEnabled) => {
				if (isCashPlusEnabled) {
					return this.cashplusFacade
						.getSubscriptionsState$()
						.pipe(map((state) => state?.subscribed && !state?.error));
				}
				return of(null);
			})
		);
	}

	public getResponseAPI(): void {
		this.isLoadingInProgress$$.next(true);
		this.cashplusFacade.fetchCombinedCashPlusState();
		this.cashplusFacade
			.getConfigurationsState$()
			.pipe(
				filter((state) => !state.error),
				take(1)
			)
			.subscribe(() => this.cashplusFacade.fetchPortfolioFiatConfigForAllFiat(true));

		setTimeout(() => {
			this.isLoadingInProgress$$.next(false);
		}, 500);
	}

	/**
	 * checks if the default walletId is eligible for Cash+ if so, navigates to it,
	 * otherwise it will search for a supported cash+ wallet with the highest balance
	 * should be used by GlobalNotification/Notifications too
	 */
	public goToFiatWallet(): void {
		this.trackingFacade.trackMoveBalanceToWalletBtnClick("home page");
		this.cashplusFacade
			.isDefaultWalletEligibleCashPlus$()
			.pipe(
				take(1),
				switchMap((isEligible) => (isEligible ? of(this.getDefaultWallet()) : this.getEligibleWallet())),
				take(1)
			)
			.subscribe((fiatWalletId) => {
				this.router.navigateByUrl(`/fiat-wallet/${fiatWalletId}`);
			});
	}

	private getDefaultWallet(): string {
		const defaultFiatId: string = this.accountService.getDefaultFiat().getId();
		return this.fiatWalletService.getWalletByFiatId(defaultFiatId).getId();
	}

	private getEligibleWallet(): Observable<string> {
		return this.getSupportedFiatAssets().pipe(
			map((portfolioAssetDetails) => {
				const supportedAssets = portfolioAssetDetails.sort(
					(a, b) => parseFloat(b.fiat_balance) - parseFloat(a.fiat_balance)
				);
				return this.fiatWalletService.getWalletByFiatId(supportedAssets[0].asset_id).getId();
			})
		);
	}

	private getSupportedFiatAssets(): Observable<PortfolioAssetDetails[]> {
		return combineLatest([
			this.portfolioFacade.getPortfolioOverviewState$(),
			this.cashplusFacade.getConfigurationsState$()
		]).pipe(
			map(([portfolioOverview, configuration]) => {
				const assetPids = configuration.configurations.map((config) => config.fiat_pid);
				return portfolioOverview.portfolios.filter(
					(assetDetails) => assetPids.indexOf(assetDetails.asset_pid) !== -1
				);
			})
		);
	}
}
